// Paths are relative to `$soulodyApi` base URL (`VITE_API_SOULODY_URL`, ends
// in `/api/v4/`) — no leading slash, no version. The endpoints live on the
// `api-soulody` service, not the legacy `$http` client.
const SAVE_ENDPOINT = 'health-observations';
const HISTORY_ENDPOINT = 'health-observations/history';
const SAVE_ERROR_CODE = '0x0000008A';
const HISTORY_ERROR_CODE = '0x0000008C';
const CONSENT_ERROR_CODE = '0x0000008B';

// BE returns `{ observations: [{ id, provider, observedAt, metrics, context }] }`;
// FE charts use the legacy `measuredAt` field name. Map at this boundary so
// chart components stay decoupled from the BE schema.
function mapObservationToMeasurement(observation) {
  return {
    measuredAt: observation?.observedAt,
    metrics: observation?.metrics ?? {},
    ...(observation?.context !== undefined && { context: observation.context })
  };
}

function rangeKey(from, to) {
  return `${from}|${to}`;
}

const state = () => ({
  saving: false,
  lastMeasurement: null,
  // Caches per-range fetch results to dedupe the 9 chart cards (one per
  // metric) requesting the same month — without it we'd fire 9 identical
  // GETs every time the overview tab mounts. Both caches are in-memory
  // only; cleared by `saveMeasurement` so a fresh measurement shows up
  // on the chart without a manual reload.
  historyCache: Object.create(null),
  historyInflight: Object.create(null)
});

const getters = {
  saving(state) {
    return state.saving;
  },
  lastMeasurement(state) {
    return state.lastMeasurement;
  }
};

const mutations = {
  setSaving(state, payload) {
    state.saving = payload;
  },
  setLastMeasurement(state, payload) {
    state.lastMeasurement = payload;
  },
  setHistoryEntry(state, { key, data }) {
    state.historyCache[key] = data;
  },
  setHistoryInflight(state, { key, promise }) {
    if (promise === null) {
      delete state.historyInflight[key];
    } else {
      state.historyInflight[key] = promise;
    }
  },
  clearHistoryCache(state) {
    state.historyCache = Object.create(null);
    state.historyInflight = Object.create(null);
  },
  reset(state) {
    state.saving = false;
    state.lastMeasurement = null;
    state.historyCache = Object.create(null);
    state.historyInflight = Object.create(null);
  }
};

const actions = {
  async saveMeasurement({ commit, dispatch }, payload) {
    commit('setSaving', true);
    try {
      const saved = await this.$soulodyApi.post(SAVE_ENDPOINT, payload);
      commit('setLastMeasurement', saved ?? payload);
      // The new measurement may belong to a range we already cached — drop
      // the cache so the next history fetch returns a fresh page.
      commit('clearHistoryCache');
      return saved ?? payload;
    } catch (error) {
      const code = error?.response?.status === 403 ? CONSENT_ERROR_CODE : SAVE_ERROR_CODE;
      dispatch('errorModal/handleError', { error, showModal: false, code }, { root: true });
      throw error;
    } finally {
      commit('setSaving', false);
    }
  },
  async fetchHistory({ commit, dispatch, state }, { from, to, profileId }) {
    // Cache & inflight key includes profileId so a future profile switch
    // (rare in this app, but possible for partner/family flows) doesn't
    // serve another user's history from the cache.
    const key = `${rangeKey(from, to)}|${profileId ?? ''}`;
    if (state.historyCache[key]) {
      return state.historyCache[key];
    }
    if (state.historyInflight[key]) {
      return state.historyInflight[key];
    }

    const promise = (async () => {
      try {
        // BE expects the profile id in the query string (mirrors the
        // `evaluations` endpoint pattern) — service-account JWT only
        // verifies signature, user identity comes from the request.
        const params = new URLSearchParams({ from, to });
        if (profileId !== undefined && profileId !== null && profileId !== '') {
          params.set('profileId', String(profileId));
        }
        const response = await this.$soulodyApi.get(`${HISTORY_ENDPOINT}?${params.toString()}`);
        const observations = Array.isArray(response?.observations) ? response.observations : [];
        const measurements = observations.map(mapObservationToMeasurement);
        commit('setHistoryEntry', { key, data: measurements });
        return measurements;
      } catch (error) {
        const code = error?.response?.status === 403 ? CONSENT_ERROR_CODE : HISTORY_ERROR_CODE;
        dispatch('errorModal/handleError', { error, showModal: false, code }, { root: true });
        throw error;
      } finally {
        commit('setHistoryInflight', { key, promise: null });
      }
    })();

    commit('setHistoryInflight', { key, promise });
    return promise;
  },
  reset({ commit }) {
    commit('reset');
  }
};

export const vitalSigns = {
  state,
  getters,
  mutations,
  actions,
  namespaced: true
};
